import { Link } from "react-router-dom";
import { IMAGES } from "../lib/site";
import {
  Search,
  Home as HomeIcon,
  BadgePercent,
  PhoneCall,
  AlertTriangle,
  ArrowUpRight,
  Check,
  Construction,
} from "lucide-react";

const SOURCING_FEES = [
  { range: "Properties up to £200,000", fee: "£500" },
  { range: "£200,001 – £400,000", fee: "£1,000" },
  { range: "£400,001 – £600,000", fee: "£1,500" },
  { range: "Over £600,000", fee: "£2,000" },
];

const SETUP_SERVICES = [
  "Furniture assembly and placement",
  "Property setup and staging",
  "Kitchen equipment organisation",
  "Bedroom preparation",
  "Curtain and blind installation",
  "Minor repairs and decorating",
  "Deep cleaning",
  "Garden and exterior tidying",
  "Final property checks before occupation",
];

const MAINTENANCE_TIERS = [
  { month: "Month 1", detail: "50% off labour charges" },
  { month: "Month 2", detail: "25% off labour charges" },
  { month: "Month 3", detail: "10% off labour charges" },
  { month: "Month 4 onwards", detail: "Standard labour rates apply" },
];

export default function Sourcing() {
  return (
    <>
      {/* Development banner */}
      <div
        className="bg-neutral-950 text-white"
        data-testid="sourcing-dev-banner"
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-center gap-3 text-center">
          <Construction size={16} className="shrink-0" />
          <p className="text-[11px] sm:text-xs tracking-[0.22em] uppercase">
            Currently Under Development · Launching Soon
          </p>
        </div>
      </div>

      {/* Hero */}
      <section
        className="relative w-full h-[56vh] min-h-[420px] overflow-hidden bg-black"
        data-testid="sourcing-hero"
      >
        <img
          src={IMAGES.cottage}
          alt="Norfolk property"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-16">
          <p className="overline text-white/80 mb-4">New service</p>
          <h1 className="font-serif-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl">
            Property Sourcing &amp; Maintenance Services
          </h1>
        </div>
      </section>

      {/* Intro / notice banner */}
      <section
        className="bg-neutral-100 border-b border-black/10"
        data-testid="sourcing-intro"
      >
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center fade-up">
          <p className="text-lg sm:text-xl leading-relaxed text-neutral-800">
            This service is currently being developed and will be launching
            soon. If you are interested in working with Bittern Property
            Services for property sourcing, setup, or ongoing maintenance,
            please contact us to register your interest.
          </p>
          <Link
            to="/contact"
            data-testid="sourcing-register-top"
            className="mt-8 inline-flex items-center gap-2 bg-black text-white hover:bg-neutral-800 transition-colors px-8 py-5 text-[11px] tracking-[0.22em] uppercase font-medium"
          >
            Register Your Interest <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      {/* Property Deal Sourcing */}
      <section
        className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-28 fade-up"
        data-testid="section-sourcing"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Search size={28} strokeWidth={1.5} />
            <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
              Property Deal Sourcing
            </h2>
            <p className="mt-6 text-neutral-600 text-lg leading-relaxed">
              Bittern Property Services will soon offer property sourcing for
              landlords, holiday-let operators, Airbnb hosts, and property
              investors.
            </p>
            <p className="mt-4 text-neutral-600 leading-relaxed">
              Our aim is to help clients identify suitable rental, holiday-let,
              and investment opportunities while providing a seamless transition
              into property setup and ongoing maintenance.
            </p>
          </div>
          <div className="md:col-span-7">
            <p className="overline text-neutral-500 mb-6">Proposed sourcing fees</p>
            <div className="border border-black/10">
              {SOURCING_FEES.map((f, i) => (
                <div
                  key={f.range}
                  className={`flex items-center justify-between px-6 py-6 ${
                    i !== SOURCING_FEES.length - 1 ? "border-b border-black/10" : ""
                  }`}
                  data-testid={`sourcing-fee-${i}`}
                >
                  <span className="text-neutral-800">{f.range}</span>
                  <span className="font-serif-display text-3xl">{f.fee}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Property Setup & Preparation */}
      <section
        className="bg-neutral-100 border-y border-black/10"
        data-testid="section-setup"
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-28 fade-up">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <HomeIcon size={28} strokeWidth={1.5} />
              <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
                Property Setup &amp; Preparation
              </h2>
              <p className="mt-6 text-neutral-600 text-lg leading-relaxed">
                Once a property has been secured, Bittern Property Services will
                offer a complete property preparation package designed to get
                the property ready for guests or tenants.
              </p>
              <p className="mt-4 text-neutral-600 leading-relaxed">
                Labour rates may be discounted for clients who have used our
                sourcing service. Materials and supplied items will be charged
                separately.
              </p>
            </div>
            <div className="md:col-span-7">
              <p className="overline text-neutral-500 mb-6">Services may include</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-black/10 border border-black/10">
                {SETUP_SERVICES.map((s) => (
                  <div
                    key={s}
                    className="bg-white px-6 py-5 flex items-start gap-3"
                    data-testid={`setup-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    <Check size={18} className="mt-0.5 shrink-0" />
                    <span className="text-neutral-800">{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Maintenance Benefits */}
      <section
        className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-28 fade-up"
        data-testid="section-maintenance"
      >
        <div className="max-w-3xl">
          <BadgePercent size={28} strokeWidth={1.5} />
          <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
            Maintenance Benefits for Sourced Properties
          </h2>
          <p className="mt-6 text-neutral-600 text-lg leading-relaxed">
            Clients who use our property sourcing service may receive the
            following introductory maintenance discounts. Material costs are
            charged separately.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {MAINTENANCE_TIERS.map((t) => (
            <div
              key={t.month}
              className="border border-black/10 p-8"
              data-testid={`maintenance-tier-${t.month.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <p className="overline text-neutral-500">{t.month}</p>
              <p className="mt-4 font-serif-display text-2xl leading-tight">
                {t.detail}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Reduced Call-out Fees */}
      <section
        className="bg-neutral-950 text-white"
        data-testid="section-callout"
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-28 fade-up">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-6">
              <PhoneCall size={28} strokeWidth={1.5} />
              <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
                Reduced Emergency Call-Out Fees
              </h2>
              <p className="mt-6 text-neutral-300 leading-relaxed">
                For properties sourced through Bittern Property Services.
                Excludes bank holidays, out-of-hours emergencies, and material
                costs.
              </p>
            </div>
            <div className="md:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border border-white/20 p-8" data-testid="callout-reduced">
                <p className="font-serif-display text-6xl leading-none">£65</p>
                <p className="mt-4 text-sm text-neutral-300 leading-relaxed">
                  Reduced call-out fee for the first 6 months.
                </p>
              </div>
              <div className="border border-white/20 p-8" data-testid="callout-standard">
                <p className="font-serif-display text-6xl leading-none">£100</p>
                <p className="mt-4 text-sm text-neutral-300 leading-relaxed">
                  Standard call-out fee thereafter.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Development Notice + CTA */}
      <section
        className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-28 text-center fade-up"
        data-testid="section-notice"
      >
        <div className="inline-flex items-center gap-3 border border-black/20 px-5 py-3 mb-8">
          <AlertTriangle size={16} />
          <span className="overline text-neutral-600">Development Notice</span>
        </div>
        <p className="text-lg sm:text-xl leading-relaxed text-neutral-800">
          <span className="font-medium">Please note:</span> This service is
          currently under development and pricing, package details, and
          availability may change before launch. We are currently accepting
          expressions of interest from landlords, investors, holiday-let
          operators, and Airbnb hosts.
        </p>
        <Link
          to="/contact"
          data-testid="sourcing-register-bottom"
          className="mt-10 inline-flex items-center gap-2 bg-black text-white hover:bg-neutral-800 transition-colors px-10 py-6 text-[11px] tracking-[0.22em] uppercase font-medium"
        >
          Register Your Interest <ArrowUpRight size={16} />
        </Link>
      </section>
    </>
  );
}
